import { useState } from 'react';
import { AlertTriangle } from 'lucide-react';

import ShareButton from '../access/ShareButton';
import {
  dismissConcern,
  escalateConcern,
  materializeConcern,
  resolveConcern,
} from '../../services/projectRegisterApi';
import {
  Btn,
  Chip,
  DetailShell,
  ErrorBanner,
  RISK_TYPE_TONE,
  SectionLabel,
  StatItem,
  T,
  concernEntryText,
  concernStatusLabel,
  fmtDate,
  fmtDateTime,
  humanise,
  isClosed,
  personLabel,
  relativeAge,
  shortRef,
  statusTone,
} from './shared';

const ACTIONS = [
  {
    key: 'escalate',
    label: 'Escalate',
    fn: escalateConcern,
    variant: 'primary',
  },
  {
    key: 'materialize',
    label: 'Mark materialised',
    fn: materializeConcern,
    variant: 'danger',
  },
  {
    key: 'resolve',
    label: 'Mark resolved',
    fn: resolveConcern,
    variant: 'default',
  },
  { key: 'dismiss', label: 'Dismiss', fn: dismissConcern, variant: 'ghost' },
];

/**
 * The right-hand drawer content for one concern (risk, dependency, or
 * blocker). `detail` is the `{ concern, related_commitments, links }`
 * envelope from `GET /concerns/{id}`. `onActed` is called with the updated
 * concern after an action succeeds, so the parent list can move the row
 * between its Open / Recently closed sections without a full refetch.
 *
 * This is the reference layout every other register detail panel
 * (Commitment/Decision) mirrors via `DetailShell` — same header/body/footer
 * shape, so the five tabs read as one product.
 */
const RiskDetailPanel = ({ accessToken, detail, onClose, onActed }) => {
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);

  if (!detail?.concern) return null;
  const {
    concern,
    related_commitments: relatedCommitments = [],
    links = [],
  } = detail;

  const compounds = links.filter((link) =>
    link.relationship?.toUpperCase().includes('COMPOUND'),
  );
  const gates = links.filter((link) =>
    link.relationship?.toUpperCase().includes('GATE'),
  );
  const compoundLinks = [...compounds, ...gates];

  const st = statusTone(concernStatusLabel(concern));
  const riskTone = RISK_TYPE_TONE[concern.risk_type] || {
    fg: T.textSec,
    bg: 'rgba(209,209,199,0.08)',
  };
  const closed = isClosed(concern);
  const attention = !!concern.ambiguity?.blocking;

  const runAction = async (action) => {
    setBusy(action.key);
    setError(null);
    try {
      const response = await action.fn(accessToken, concern.entity_id);
      onActed?.(response.concern);
    } catch (actionError) {
      setError(String(actionError?.message || actionError));
    } finally {
      setBusy(null);
    }
  };

  return (
    <DetailShell
      chips={
        <>
          <Chip fg={riskTone.fg} bg={riskTone.bg}>
            {concern.risk_type || concern.subtype}
          </Chip>
          <Chip bordered>{humanise(concern.subtype)}</Chip>
        </>
      }
      statusChip={
        <Chip fg={st.fg} bg={st.bg}>
          {st.label}
        </Chip>
      }
      title={concern.description}
      raisedIn={
        concern.created_at
          ? `A meeting transcript · ${fmtDateTime(concern.created_at)}`
          : 'A meeting transcript'
      }
      quote={concern.evidence?.quote}
      onClose={onClose}
      footer={
        closed ? (
          <span className="text-[12px]" style={{ color: T.muted }}>
            Closed — no actions pending
          </span>
        ) : (
          <div className="flex flex-wrap gap-2">
            <ShareButton
              itemType="concern"
              itemId={concern.entity_id}
              title={concern.description}
            />
            {ACTIONS.map((action) => (
              <Btn
                key={action.key}
                variant={action.variant}
                disabled={busy !== null}
                onClick={() => runAction(action)}
              >
                {busy === action.key ? (
                  <span className="flex items-center gap-1.5">
                    <AlertTriangle className="h-3.5 w-3.5 animate-pulse" />
                    {action.label}
                  </span>
                ) : (
                  action.label
                )}
              </Btn>
            ))}
          </div>
        )
      }
    >
      {error && (
        <div className="mb-3">
          <ErrorBanner>{error}</ErrorBanner>
        </div>
      )}

      {attention && (
        <div
          className="mb-3 rounded-xl p-3"
          style={{
            background: T.decisionBg,
            border: '1px solid rgba(196,181,253,0.35)',
          }}
        >
          <div className="flex flex-wrap items-center gap-1.5">
            {(concern.ambiguity.flags || []).map((flag) => (
              <Chip key={flag} fg={T.decision} bordered>
                {humanise(flag)}
              </Chip>
            ))}
          </div>
          <p className="mt-2 text-[13px]" style={{ color: T.text }}>
            {concern.ambiguity.question_text}
          </p>
          {concern.ambiguity.route_to_person_id && (
            <p className="mt-1.5 text-[11px]" style={{ color: T.muted }}>
              Routed to {personLabel(concern.ambiguity.route_to_person_id)} ·
              not committed until resolved
            </p>
          )}
        </div>
      )}

      {/* One shared border/bg for the whole stat block instead of nine
          individually-bordered fields — same treatment as the Insights and
          Commitment detail panels' stat grids. StatItem hides any field
          this concern has no value for, rather than a "—" placeholder. */}
      <div className="shadow-xs mb-3 grid grid-cols-2 gap-x-4 gap-y-3 rounded-xl border border-zinc-700/60 bg-zinc-800/50 p-2.5 sm:grid-cols-3">
        <StatItem label="Cause" value={concern.cause} />
        <StatItem
          label="Criticality"
          value={
            concern.risk_score?.composite_impact
              ? humanise(concern.risk_score.composite_impact)
              : null
          }
          tone={T.warn}
        />
        <StatItem
          label="Combined score"
          value={concern.risk_score?.combined_score?.toFixed(2)}
        />
        <StatItem label="Affects" value={concern.linked_deliverable_id} />
        <StatItem
          label="Also gates"
          value={concern.gated_deliverables?.join(', ')}
        />
        <StatItem
          label="Responsible"
          value={personLabel(concern.responsible_person_id) || 'Unassigned'}
          tone={concern.responsible_person_id ? undefined : T.warn}
        />
        <StatItem
          label="Accountable"
          value={personLabel(concern.accountable_person_id)}
        />
        <StatItem
          label="Raised by"
          value={personLabel(concern.raiser_person_id)}
        />
        <StatItem
          label="Age"
          value={relativeAge(concern.created_at || concern.submission_date)}
        />
      </div>

      {compoundLinks.length > 0 && (
        <div className="mb-3">
          <SectionLabel>Compounds with</SectionLabel>
          <div className="space-y-1.5">
            {compoundLinks.map((link) => (
              <div
                key={link.entity_link_id}
                className="rounded-xl border border-zinc-700/60 bg-zinc-800/50 px-3 py-2.5 text-[13px] text-zinc-200"
              >
                <span className="font-mono text-[11px] text-zinc-400">
                  {shortRef(
                    link.source_entity_id === concern.entity_id
                      ? link.target_entity_id
                      : link.source_entity_id,
                  )}
                </span>{' '}
                ·{' '}
                <span className="font-semibold text-zinc-300">
                  {humanise(link.relationship)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {concern.mitigation_actions?.length > 0 && (
        <div className="mb-3">
          <SectionLabel count={concern.mitigation_actions.length}>
            Mitigation discussed
          </SectionLabel>
          <ul className="space-y-1.5">
            {concern.mitigation_actions.map((action, index) => (
              <li
                key={index}
                className="flex items-start gap-2.5 rounded-xl border border-zinc-700/60 bg-zinc-800/50 p-2.5 text-[13px] text-zinc-200"
              >
                <span className="text-indigo-400">⚡</span>
                <span className="min-w-0">{concernEntryText(action)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {relatedCommitments.length > 0 && (
        <div className="mb-3">
          <SectionLabel count={relatedCommitments.length}>
            Related commitments
          </SectionLabel>
          <div className="space-y-1.5">
            {relatedCommitments.map((commitment) => (
              <div
                key={commitment.entity_id}
                className="flex items-center justify-between gap-3 rounded-xl border border-zinc-700/60 bg-zinc-800/50 p-2.5 text-[13px]"
              >
                <div className="min-w-0">
                  <div className="truncate font-semibold text-zinc-100">
                    {commitment.normalised_text ||
                      shortRef(commitment.entity_id)}
                  </div>
                  <div className="mt-0.5 text-[11px] text-zinc-400">
                    <span className="font-medium text-zinc-300">
                      {personLabel(commitment.committer_person_id)}
                    </span>
                    {commitment.deadline_date &&
                      ` · due ${fmtDate(commitment.deadline_date)}`}
                  </div>
                </div>
                <Chip bordered>{humanise(commitment.status)}</Chip>
              </div>
            ))}
          </div>
        </div>
      )}

      {concern.discussions?.length > 0 && (
        <div className="mb-3">
          <SectionLabel count={concern.discussions.length}>
            Discussions
          </SectionLabel>
          <ul className="space-y-1.5">
            {concern.discussions.map((item, index) => (
              <li
                key={index}
                className="flex items-start gap-2 rounded-xl border border-zinc-800 bg-zinc-800/30 p-2.5 text-[13px] text-zinc-300"
              >
                <span className="text-zinc-500">💬</span>
                <span className="min-w-0">{concernEntryText(item)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {concern.history?.length > 0 && (
        <div>
          <SectionLabel count={concern.history.length}>
            Change History
          </SectionLabel>
          <div className="space-y-1.5 rounded-xl border border-zinc-700/60 bg-zinc-800/50 p-2.5">
            {concern.history.map((entry, index) => (
              <p key={index} className="text-[12px] text-zinc-300">
                {entry.at && (
                  <span className="text-zinc-500">{fmtDate(entry.at)} · </span>
                )}
                <span className="font-semibold text-zinc-200">
                  {humanise(entry.update_type)}
                </span>
                {entry.delta_summary && ` — ${entry.delta_summary}`}
              </p>
            ))}
          </div>
        </div>
      )}
    </DetailShell>
  );
};

export default RiskDetailPanel;
